import { useCallback, useMemo, useRef, useState } from 'react';
import { composeLyrics, parseLyrics } from './mvData';
import {
  buildLyricsPayload,
  buildMusicDraft,
  buildMusicPayload,
  readErrorMessage,
} from './mvAudioPayloads';
import {
  generateLyrics as requestLyrics,
  generateImageAudio,
  generateTextAudio,
  pollAudioGeneration,
  updateLyrics as updateLyricsRecord,
} from '../../../services/musicAudioService';

const INITIAL = {
  idea: '',
  mood: '',
  language: '',
  musicPrompt: '',
  style: '',
  musicMood: '',
  vocal: '',
  musicMode: '',
  duration: '180',
  tempo: 'Medium',
  ratio: '9:16',
  resolution: '1080p',
  visual: 'Cinematic',
  mvPrompt: '',
};

const PROGRESS_DURATION_MS = {
  lyrics: 8000,
  music: 20000,
  video: 180000,
};

export default function useMvGenerator() {
  const [step, setStep] = useState('lyrics');
  const [values, setValues] = useState(INITIAL);
  const [lyrics, setLyrics] = useState([]);
  const [lyricsRecordId, setLyricsRecordId] = useState('');
  const [isEditingLyrics, setIsEditingLyrics] = useState(false);
  const [music, setMusic] = useState(null);
  const [mv, setMv] = useState(null);
  const [musicImages, setMusicImages] = useState([]);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [retryStep, setRetryStep] = useState(null);
  const [error, setError] = useState('');
  const touchedKeys = useRef(new Set());

  const canGenerate = useMemo(() => {
    if (step === 'lyrics') return values.idea.trim().length > 0;
    if (step === 'music') return lyrics.length > 0 || values.musicPrompt.trim().length > 0;
    return !!music && images.length > 0;
  }, [images.length, lyrics.length, music, step, values.idea, values.musicPrompt]);

  const updateValue = (key, value) => {
    touchedKeys.current.add(key);
    setValues((prev) => ({ ...prev, [key]: value }));
  };
  const clearError = useCallback(() => setError(''), []);
  const applyOptionDefaults = useCallback((optionMap = {}) => {
    setValues((prev) => ensureOptionDefaults(
      prev,
      optionMap,
      touchedKeys.current,
    ));
  }, []);
  const hasResult = (target = step) => (
    target === 'lyrics' ? lyrics.length > 0 : target === 'music' ? !!music : !!mv
  );
  const showGenerate = !hasResult() || retryStep === step || loading;
  const nextStep = () => setStep(step === 'lyrics' ? 'music' : 'video');

  const startRegenerate = (target = step) => {
    setStep(target);
    setRetryStep(target);
    if (target === 'lyrics') generateLyrics();
    if (target === 'music') generateMusic();
    if (target === 'video') generateMv();
  };

  const updateLyricSections = (next) => {
    setLyrics(next);
    setMusic(null);
    setMv(null);
  };

  const toggleLyricsEditing = async () => {
    if (!isEditingLyrics) return setIsEditingLyrics(true);
    const nextText = composeLyrics(lyrics);
    if (lyricsRecordId && !await saveLyricsToBackend(nextText)) return;
    setIsEditingLyrics(false);
  };

  async function generateLyrics() {
    await run('lyrics', async () => {
      const result = await requestLyrics(buildLyricsPayload(values));
      const sections = parseLyrics(result.lyrics);
      if (!sections.length) throw new Error('No lyrics returned');
      setLyrics(sections);
      setLyricsRecordId(result.id || '');
      setIsEditingLyrics(false);
      setMusic(null);
      setMv(null);
      setRetryStep(null);
    });
  }

  async function generateMusic() {
    await run('music', async () => {
      const lyricsText = composeLyrics(lyrics);
      const payload = buildMusicPayload(values, lyricsText, musicImages.length > 0);
      const created = musicImages.length
        ? await generateImageAudio(musicImages, payload)
        : await generateTextAudio(payload);
      if (!created.id) throw new Error('No audio task id returned');
      const record = await pollAudioGeneration(created.id);
      setMusic(buildMusicDraft(record, values, lyricsText));
      setMv(null);
      setRetryStep(null);
    });
  }

  async function generateMv() {
    await run('video', async () => {
      const prompt = values.mvPrompt.trim();
      setMv({
        title: prompt || `${values.idea || 'Catoo'} MV`,
        meta: `${values.ratio} • ${values.resolution} • ${values.visual}`,
        prompt,
      });
      setRetryStep(null);
    });
  }

  async function run(target, task) {
    setLoading(true);
    setError('');
    setGenerationProgress(8);
    const startedAt = Date.now();
    const duration = PROGRESS_DURATION_MS[target] || PROGRESS_DURATION_MS.video;
    const timer = window.setInterval(() => {
      const elapsed = Date.now() - startedAt;
      setGenerationProgress(Math.min(95, 8 + Math.round((elapsed / duration) * 87)));
    }, 100);
    try {
      await task();
      setGenerationProgress(100);
    } catch (err) {
      setError(readErrorMessage(err));
    } finally {
      window.clearInterval(timer);
      setLoading(false);
    }
  }

  async function saveLyricsToBackend(text) {
    try {
      const result = await updateLyricsRecord(lyricsRecordId, text);
      if (result.lyrics) setLyrics(parseLyrics(result.lyrics));
      return true;
    } catch (err) {
      setError(readErrorMessage(err));
      return false;
    }
  }

  return {
    step, setStep, values, updateValue, lyrics, setLyrics: updateLyricSections,
    isEditingLyrics, setIsEditingLyrics, toggleLyricsEditing, music, mv,
    musicImages, setMusicImages, images, setImages, loading,
    generationProgress, canGenerate, hasResult, showGenerate,
    nextStep, startRegenerate, generateLyrics, generateMusic, generateMv, error,
    applyOptionDefaults, clearError,
  };
}

function ensureOptionDefaults(values, optionMap, touchedKeys) {
  let changed = false;
  const next = { ...values };
  const keyMap = { musicMood: 'mood', musicMode: 'imageMode' };
  ['mood', 'language', 'style', 'musicMood', 'vocal', 'musicMode', 'duration'].forEach((key) => {
    const options = optionMap[keyMap[key] || key] || [];
    if (!options.length || touchedKeys.has(key)) return;
    if (options.some((item) => item.value === next[key])) return;
    next[key] = options[0].value;
    changed = true;
  });
  return changed ? next : values;
}
